import React, { Fragment, useEffect, useState, useRef, useMemo } from "react";
import TuneIcon from "@mui/icons-material/Tune";
import styles from "./styles.module.css";
import Pagination from "../../Pagination";

const TableViewResumo = ({ columns, data, hasBotoes }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [filteredData, setFilteredData] = useState(data);
  const [visibleItems, setVisibleItems] = useState([]);
  const [loadedChunks, setLoadedChunks] = useState(0);
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const CHUNK_SIZE = 20;

  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleStyleNumerico = (item) => {
    if (typeof item === "string") {
      switch (true) {
        case item.includes("%"): {
          const valor = Number(item.replace("%", "").replace(",", "."));
          if (!isNaN(valor))
            return { textAlign: "right", whiteSpace: "nowrap" };
          return { textAlign: "center", whiteSpace: "nowrap" };
        }
        case item.includes("R$"): {
          const valor = Number(
            item.replace("R$", "").replace(",", ".").replaceAll(".", "")
          );
          if (!isNaN(valor))
            return { textAlign: "right", whiteSpace: "nowrap" };
          return { textAlign: "center", whiteSpace: "nowrap" };
        }
        default:
          break;
      }
    }
  };

  useEffect(() => {
    setLoadedChunks(0);
    setVisibleItems([]);
    loadingRef.current = false;
  }, [filteredData]);

  useEffect(() => {
    if (filteredData.length > 0 && !loadingRef.current) {
      loadingRef.current = true;
      const endIndex = Math.min(
        (loadedChunks + 1) * CHUNK_SIZE,
        filteredData.length
      );
      const newItems = filteredData.slice(0, endIndex);
      setVisibleItems(newItems);
      loadingRef.current = false;
    }
  }, [filteredData, loadedChunks]);

  useEffect(() => {
    if (!observerRef.current || visibleItems.length >= filteredData.length)
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setLoadedChunks((prev) => prev + 1);
      },
      { threshold: 0.1 }
    );

    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [visibleItems, filteredData]);

  const projetoColIndex = useMemo(() => {
    const normalize = (s) =>
      typeof s === "string"
        ? s
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
        : "";
    const idx = columns.findIndex((c) =>
      normalize(c.label).includes("composicao projeto")
    );
    return idx >= 0 ? idx : columns.length - 1;
  }, [columns]);

  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={i}
                className={`${styles.columns} ${
                  i === projetoColIndex && !hasBotoes
                    ? styles.stickyRightHeader
                    : ""
                }`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((row, rowIndex) => (
            <Fragment key={rowIndex}>
              <tr
                className={styles.tableBody}
                style={row?.style ?? {}}
                ref={rowIndex === visibleItems.length - 1 ? observerRef : null}
              >
                {columns.map((col, colIndex) => (
                  <td
                    key={colIndex}
                    className={
                      colIndex === projetoColIndex && !hasBotoes
                        ? styles.stickyRight
                        : ""
                    }
                    style={handleStyleNumerico(row[col.key])}
                  >
                    {typeof row[col.key] === "function"
                      ? row[col.key]()
                      : row[col.key]}
                  </td>
                ))}
                {hasBotoes ? (
                  <td>
                    <div className={styles.btnContainer}>
                      {row.btn.length === 1 ? (
                        <button
                          className={styles.uniqueBtn}
                          onClick={row.btn[0].onClick}
                        >
                          <span style={{ marginRight: ".5rem" }}>
                            {row.btn[0].icon}
                          </span>
                          {row.btn[0].title}
                        </button>
                      ) : (
                        <button
                          className={styles.btnAction}
                          onClick={() => handleRowClick(rowIndex)}
                        >
                          <TuneIcon sx={{ marginRight: ".5rem" }} />
                          Opções
                        </button>
                      )}
                    </div>
                  </td>
                ) : (
                  ""
                )}
              </tr>
              {expandedRow === rowIndex && (
                <tr>
                  <td colSpan={columns.length + 1} className={styles.tableBody}>
                    <div className={styles.btns}>
                      {row.btn.map((item, index) => (
                        <button
                          className={styles.btn}
                          key={index}
                          onClick={item.onClick}
                        >
                          <span>{item.icon}</span>
                          {item.title}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      <div className={styles.paginationContainer}>
        <Pagination
          data={data}
          filteredData={filteredData}
          setFilteredData={setFilteredData}
        />
      </div>
    </>
  );
};

export default TableViewResumo;
